import { Mic, Utensils, Refrigerator } from "lucide-react";
import TestimonialCarousel from "@/components/marketing/TestimonialCarousel";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AiChatDemo } from "@/components/marketing/product-demo/AiChatDemo";
import NomsyProductDemo from "@/components/marketing/product-demo/NomsyProductDemo";

const APP_STORE_URL = "https://apps.apple.com/";
const TEMPLATE_SIX_PRODUCT_SCREENS = [
  {
    id: "voice-guidance",
    title: "Log food by just talking.",
    detail: "Log meals and make changes with your voice.",
    cardClassName: "text-[#382d25]",
  },
  {
    id: "menu-guidance",
    title: "Take a photo of the restaurant menu.",
    detail: "Know what to order and how to customize it.",
    cardClassName: "text-[#292845]",
  },
  {
    id: "fridge-guidance",
    title: "Take a photo of your fridge.",
    detail: "See what to eat to get the nutrients you need.",
    cardClassName: "text-[#21352b]",
  },
] as const;
const TEMPLATE_SIX_HERO_VIDEO_SRC = "/template6-media/hero-street-phone.mp4";
const TEMPLATE_SIX_HERO_VIDEO_POSTER = "/template6-media/hero-street-phone-poster.jpg";
const HERO_AI_NAMES = ["Claude", "ChatGPT", "Grok", "Gemini", "Claude", "ChatGPT", "Perplexity", "Mistral"] as const;

const LANDING_FAQS = [
  {
    question: "What does Nomsy cost?",
    answer: <>Nomsy has a Free plan and an optional Premium subscription. Free includes up to seven AI scans per week. You can try a seven-day Premium Preview with up to 40 AI scans, without a payment card or automatic renewal. The App Store shows your local Premium price and any eligible subscription trial before you confirm.</>,
  },
  {
    question: "Which devices can I use?",
    answer: <>Nomsy is built for iPhone and requires iOS 18 or later. There is currently no Android app.</>,
  },
  {
    question: "How much effort does meal logging take?",
    answer: <>Snap a photo or describe your meal by voice or text. Nomsy uses AI to identify the food and estimate its nutrients. You can review and correct the foods and amounts, so your log reflects what you actually ate.</>,
  },
  {
    question: "What makes Nomsy hyper-personal?",
    answer: <>Nomsy tracks 67 nutrients. The ranges for Too Low, On Target, and Too High are based on international nutrition standards and personalized using the profile you provide, your goals, and your dietary needs. Your logged meals help Nomsy suggest what to eat next. You can update your profile and goals in Settings as your needs change.</>,
  },
  {
    question: "Do I need ChatGPT or Claude?",
    answer: <>No. Nomsy provides food tracking, nutrient insights, and suggestions in the app. Connecting your own AI is optional. If you connect one, its access to your Nomsy food log is read-only, and you can disconnect it in Settings → AI Connections. <a className="text-[#6157ff] underline underline-offset-4" href="/connect-ai">See how AI connections work.</a></>,
  },
  {
    question: "How is my data used?",
    answer: <>Nomsy uses your profile and meal data to provide its features, including AI analysis through service providers such as OpenAI. We do not sell your personal data. You can delete your account in Settings → Security. <a className="text-[#6157ff] underline underline-offset-4" href="/legal/privacy-policy">Read the Privacy Policy</a> for details about processing, sharing, and retention.</>,
  },
];


const RotatingAiName = () => {
  const slotRef = useRef<HTMLSpanElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const [index, setIndex] = useState(0);

  useLayoutEffect(() => {
    const slot = slotRef.current;
    const label = labelRef.current;
    if (!slot || !label) return;
    let cancelled = false;
    let animation: Animation | undefined;
    const measure = () => {
      slot.style.width = `${label.getBoundingClientRect().width}px`;
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(label);
    void document.fonts?.ready.then(() => { if (!cancelled) measure(); });

    let currentIndex = 0;
    let timer: number;
    const rotate = async () => {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        timer = window.setTimeout(rotate, 2000);
        return;
      }
      // Hide the outgoing name before moving the suffix, then reveal the new
      // name only after the width has settled. Text never shares moving space.
      animation = label.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 120, fill: "forwards" });
      await animation.finished.catch(() => undefined);
      if (cancelled) return;
      const oldWidth = slot.getBoundingClientRect().width;
      const nextIndex = (currentIndex + 1) % HERO_AI_NAMES.length;
      currentIndex = nextIndex;
      label.textContent = HERO_AI_NAMES[nextIndex];
      const nextWidth = label.getBoundingClientRect().width;
      slot.style.width = `${nextWidth}px`;
      const overshoot = Math.sign(nextWidth - oldWidth) * Math.min(12, Math.max(5, Math.abs(nextWidth - oldWidth) * 0.18));
      animation = slot.animate([
        { width: `${oldWidth}px`, offset: 0, easing: "cubic-bezier(0.22, 0.8, 0.36, 1)" },
        { width: `${nextWidth + overshoot}px`, offset: 0.4, easing: "ease-in-out" },
        { width: `${nextWidth - overshoot * 0.55}px`, offset: 0.6, easing: "ease-in-out" },
        { width: `${nextWidth + overshoot * 0.28}px`, offset: 0.76, easing: "ease-in-out" },
        { width: `${nextWidth - overshoot * 0.12}px`, offset: 0.9, easing: "ease-in-out" },
        { width: `${nextWidth}px`, offset: 1 },
      ], { duration: 600 });
      await animation.finished.catch(() => undefined);
      if (cancelled) return;
      label.getAnimations().forEach((item) => item.cancel());
      setIndex(nextIndex);
      animation = label.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 120 });
      await animation.finished.catch(() => undefined);
      if (!cancelled) timer = window.setTimeout(rotate, 2000);
    };
    timer = window.setTimeout(rotate, 2000);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      observer.disconnect();
      animation?.cancel();
      label.getAnimations().forEach((item) => item.cancel());
    };
  }, []);

  return (
    <span ref={slotRef} aria-hidden="true" className="rw-ai-name-rotator" data-testid="hero-ai-name-rotator">
      <span ref={labelRef} className="rw-ai-name-label">{HERO_AI_NAMES[index]}</span>
    </span>
  );
};

const HeroNextAction = () => {
  const lineRef = useRef<HTMLSpanElement>(null);
  const contentRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const line = lineRef.current;
    const content = contentRef.current;
    const heading = line?.closest('h1');
    if (!line || !content || !heading) return;
    let cancelled = false;
    const fit = () => {
      // Fit the final line, then apply its size to the whole headline.
      heading.style.removeProperty('font-size');
      const baseSize = parseFloat(getComputedStyle(heading).fontSize);
      const width = content.getBoundingClientRect().width;
      if (width > line.clientWidth) {
        heading.style.fontSize = `${baseSize * line.clientWidth / width}px`;
      }
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(line);
    void document.fonts?.ready.then(() => { if (!cancelled) fit(); });
    return () => { cancelled = true; observer.disconnect(); };
  }, []);

  return (
    <span ref={lineRef} aria-hidden="true" className="rw-hero-next-action-line" data-testid="hero-next-action-line">
      <span ref={contentRef} className="rw-hero-next-action-content">
        <span className="rw-hero-next-action-arrow" />
        <span>You actually eat healthy.</span>
      </span>
    </span>
  );
};


const configureAutoplayVideo = (video: HTMLVideoElement) => {
  video.muted = true;
  video.defaultMuted = true;
  video.autoplay = true;
  video.playsInline = true;
  video.loop = true;
  video.volume = 0;
  video.setAttribute("muted", "");
  video.setAttribute("autoplay", "");
  video.setAttribute("playsinline", "");
};

const ExactCopyTemplate = () => {
  const heroVideoRef = useRef<HTMLVideoElement | null>(null);
  const [isHeroVideoPlaying, setIsHeroVideoPlaying] = useState(false);
  const [activeFeature, setActiveFeature] = useState('voice-guidance');
  const featureSwipe = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const selectLinkedFeature = () => {
      const id = window.location.hash.slice(1);
      if (TEMPLATE_SIX_PRODUCT_SCREENS.some(screen => screen.id === id)) setActiveFeature(id);
    };
    selectLinkedFeature();
    window.addEventListener('hashchange', selectLinkedFeature);
    return () => window.removeEventListener('hashchange', selectLinkedFeature);
  }, []);
  const testimonials = [
    {
      name: "Jonas L.",
      quote: "I just say what I ate. That’s made me actually keep logging my food.",
      tone: "bg-[#6555ed] text-white md:col-span-7",
    },
    {
      name: "Kati K.",
      quote: "‘Actually, I ate half of that.’ That’s all I have to say. I love it!",
      tone: "bg-[#f1ebe2] text-[#292720] md:col-span-5",
    },
    {
      name: "Katharina A.",
      quote: "I don’t need another score. I need help choosing lunch.",
      tone: "bg-[#eeecfc] text-[#332c65] md:col-span-5",
    },
    {
      name: "Victor R.",
      quote: "Super easy to connect Nomsy MCP to my Claude. I’ve set up really cool automations using that data now.",
      tone: "bg-[#25252b] text-white md:col-span-7",
    },
  ];
  useEffect(() => {
    const video = heroVideoRef.current;
    if (!video) return;

    configureAutoplayVideo(video);
    video.load();

    const ensurePlayback = async () => {
      if (document.visibilityState === "hidden") return;

      try {
        configureAutoplayVideo(video);
        await video.play();
        if (!video.paused) {
          setIsHeroVideoPlaying(true);
        }
      } catch {
        setIsHeroVideoPlaying(false);
      }
    };

    const handlePlaying = () => {
      setIsHeroVideoPlaying(true);
    };

    const handleWaiting = () => {
      setIsHeroVideoPlaying(false);
    };

    void ensurePlayback();

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      void ensurePlayback();
    } else {
      video.addEventListener("loadedmetadata", ensurePlayback);
      video.addEventListener("loadeddata", ensurePlayback);
      video.addEventListener("canplay", ensurePlayback);
      video.addEventListener("canplaythrough", ensurePlayback);
    }

    video.addEventListener("playing", handlePlaying);
    video.addEventListener("pause", handleWaiting);
    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("stalled", handleWaiting);
    video.addEventListener("suspend", handleWaiting);

    document.addEventListener("visibilitychange", ensurePlayback);
    window.addEventListener("focus", ensurePlayback);

    return () => {
      video.removeEventListener("loadedmetadata", ensurePlayback);
      video.removeEventListener("loadeddata", ensurePlayback);
      video.removeEventListener("canplay", ensurePlayback);
      video.removeEventListener("canplaythrough", ensurePlayback);
      video.removeEventListener("playing", handlePlaying);
      video.removeEventListener("pause", handleWaiting);
      video.removeEventListener("waiting", handleWaiting);
      video.removeEventListener("stalled", handleWaiting);
      video.removeEventListener("suspend", handleWaiting);
      document.removeEventListener("visibilitychange", ensurePlayback);
      window.removeEventListener("focus", ensurePlayback);
    };
  }, []);

  const floatingMockupSection = (
    <section
      className="scroll-mt-28 py-0"
      data-testid="template-six-floating-mockup"
      id="template-six-floating-mockup"
    >
      <div className="rw-container">
        <div className="nc-showcase-background relative mx-auto overflow-clip rounded-b-[28px] px-3 py-16 sm:px-6 md:px-10 lg:px-14 lg:py-20">
          <div className="relative z-10">
            <div className="nc-hero-stage relative order-2 flex min-h-[340px] flex-col items-center justify-center" data-testid="template-six-floating-preview">
              <div className="nc-desktop-chat nc-desktop-claude"><AiChatDemo assistant="claude" /></div>
              <NomsyProductDemo />
              <div className="nc-desktop-chat nc-desktop-chatgpt"><AiChatDemo assistant="chatgpt" /></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  return (
    <div className="rw-template6-shell bg-white text-[#101010]" data-testid="template-six-root">
      <header className="fixed inset-x-0 top-0 z-40 bg-[#ffffffcc] transition-all duration-200 backdrop-blur supports-[backdrop-filter]:bg-[#fffffff2]">
        <div className="rw-container flex h-[82px] items-center justify-between lg:h-[84px]">
          <div className="relative z-20 lg:w-2/12">
            <a className="inline-flex items-center" href="/" aria-label="Nomsy home">
              <img
                src="/images/nomsy-logo-blue.png"
                alt="Nomsy"
                className="h-[40px] w-auto brightness-0 sm:h-[42px]"
              />
            </a>
          </div>

          <div className="relative z-20 flex items-center justify-end lg:w-2/12">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full border border-black bg-white px-4 py-2 text-sm leading-5 text-black transition-all duration-150 hover:opacity-90"
            >
              Download App
            </a>
          </div>
        </div>
      </header>

      <main className="rw-template6-main pt-[90px] lg:pt-[96px]">
        <section className="py-0 transition-all duration-200" data-header-color="dark">
          <div className="rw-container">
            <div className="relative mx-auto min-h-[500px] w-full overflow-hidden rounded-[14px] border border-black/10 bg-black/20 sm:min-h-[560px] lg:min-h-[700px]">
              <video
                ref={heroVideoRef}
                className={`absolute inset-0 h-full w-full scale-x-[-1] transform object-cover transition-opacity duration-300 ${isHeroVideoPlaying ? "visible opacity-100" : "invisible opacity-0"}`}
                src={TEMPLATE_SIX_HERO_VIDEO_SRC}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                poster={TEMPLATE_SIX_HERO_VIDEO_POSTER}
                disablePictureInPicture
                disableRemotePlayback
                controls={false}
              />
              <img
                src={TEMPLATE_SIX_HERO_VIDEO_POSTER}
                alt=""
                aria-hidden="true"
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${isHeroVideoPlaying ? "opacity-0" : "opacity-100"}`}
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/70" />
              <div className="pointer-events-none relative z-10 flex min-h-[500px] items-end px-6 pb-10 pt-24 text-white sm:min-h-[560px] lg:min-h-[700px] lg:px-20 lg:pb-14 lg:pt-28">
                <div className="min-w-0 w-full max-w-[1360px]">
                  <div>
                    <h1 className="text-[min(clamp(2.55rem,5vw,4.75rem),10.5vw)] font-semibold leading-[0.97] tracking-[-0.025em] text-white">
                      Nomsy tracks.
                      <br />
                      <span className="sr-only">Claude, ChatGPT, Grok, Gemini, Perplexity, and Mistral think.</span>
                      <span aria-hidden="true" className="rw-ai-name-line inline-flex items-baseline whitespace-nowrap">
                        <RotatingAiName />
                        <span>&nbsp;thinks.</span>
                      </span>
                      <br />
                      <span className="sr-only">You actually eat healthy.</span>
                      <HeroNextAction />
                    </h1>
                    <p className="mt-6 max-w-[700px] text-[16px] leading-[1.3] tracking-[-0.025em] text-white sm:text-[18px] lg:text-[20px]">
                      The first Nutrition AI that really knows you. Deeply.{" "}
                      <br className="hidden lg:block" />
                      Track 67 nutrients. Reach hyper-personal goals.
                    </p>
                    <p className="mt-3 max-w-[660px] text-[16px] leading-[1.35] tracking-[-0.015em] text-white/82 sm:text-[18px] lg:text-[20px]">
                      Connect your own AI account to Nomsy&apos;s free MCP
                    </p>
                    <a
                      href={APP_STORE_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="rw-hero-download pointer-events-auto mt-6 inline-flex items-center rounded-full bg-white px-6 py-3 text-[15px] font-medium text-black"
                    >
                      <span className="rw-hero-download-border" aria-hidden="true" />
                      Download App
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {floatingMockupSection}

        <section className="pb-16 pt-0 lg:pb-24" id="inside-nomsy" data-testid="template-six-product-tour">
          <div className="rw-container">
            <div className="mx-auto max-w-[1240px]">
              <div className="max-w-[760px] lg:max-w-none">
                <h2 className="font-semibold text-[clamp(2.4rem,5vw,4.7rem)] leading-[0.92] tracking-[-0.025em] text-[#242424]">
                  See which nutrients you need - and what to eat to get them.
                </h2>
              </div>

              <div className="nc-feature-switcher" role="group" aria-label="Choose a feature">
                {TEMPLATE_SIX_PRODUCT_SCREENS.map((screen, index) => (
                  <button key={screen.id} type="button" aria-pressed={activeFeature === screen.id}
                    aria-controls={screen.id} onClick={() => setActiveFeature(screen.id)}>
                    {index === 0 ? <Mic aria-hidden="true" /> : index === 1 ? <Utensils aria-hidden="true" /> : <Refrigerator aria-hidden="true" />}
                    {['Voice', 'Restaurant', 'Fridge'][index]}
                  </button>
                ))}
              </div>
              <p className="nc-feature-swipe-hint">Tap a tab or swipe to explore</p>
              <div className="nc-feature-grid"
                onTouchStart={(event) => {
                  const touch = event.touches[0];
                  featureSwipe.current = window.innerWidth < 1024 && event.touches.length === 1
                    ? { x: touch.clientX, y: touch.clientY } : null;
                }}
                onTouchCancel={() => { featureSwipe.current = null; }}
                onTouchEnd={(event) => {
                  const start = featureSwipe.current;
                  featureSwipe.current = null;
                  const touch = event.changedTouches[0];
                  if (!start || !touch) return;
                  const dx = touch.clientX - start.x;
                  const dy = touch.clientY - start.y;
                  if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
                  setActiveFeature(current => {
                    const index = TEMPLATE_SIX_PRODUCT_SCREENS.findIndex(item => item.id === current);
                    const next = Math.max(0, Math.min(2, index + (dx < 0 ? 1 : -1)));
                    return TEMPLATE_SIX_PRODUCT_SCREENS[next].id;
                  });
                }}>

                {TEMPLATE_SIX_PRODUCT_SCREENS.map((screen) => (
                  <article
                    key={screen.id}
                    id={screen.id}
                    data-active={activeFeature === screen.id}
                    className="nc-feature-card relative scroll-mt-28 lg:row-span-2 lg:grid lg:grid-rows-[subgrid] lg:gap-y-0"
                  >
                    <div className="relative z-10">
                      <h3 className="font-semibold text-[25px] leading-[0.98] tracking-[-0.025em] sm:text-[28px]">
                        {screen.title}
                      </h3>
                      <p className="mt-3 max-w-[330px] text-[16px] leading-[1.4] tracking-[-0.015em] opacity-80">
                        {screen.detail}
                      </p>
                    </div>

                    <div className="nc-feature-phone relative z-10 mt-8 flex justify-center">
                      <NomsyProductDemo feature={screen.id} />
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="pb-16 pt-2 lg:pb-24 lg:pt-8" id="ai" data-testid="template-six-ai-extension">
          <div className="rw-container">
            <div className="mx-auto grid max-w-[1240px] gap-5 lg:grid-cols-[1.12fr_0.88fr]">
              <article className="relative lg:min-h-[430px] overflow-hidden rounded-[24px] bg-[#111111] px-7 py-8 text-white sm:px-10 sm:py-10 lg:px-12 lg:py-12">
                <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-28 h-[340px] w-[340px] rounded-full bg-[#8ea985]/24 blur-[100px]" />
                <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 left-10 h-[280px] w-[280px] rounded-full bg-[#d49764]/20 blur-[110px]" />
                <div className="relative z-10 flex h-full flex-col gap-6 lg:justify-between lg:gap-16">
                  <div>
                    <h2 className="font-semibold mt-5 max-w-[650px] text-[clamp(2.4rem,5.4vw,4.9rem)] leading-[0.91] tracking-[-0.025em] text-white">
                      Connect Nomsy to your own AI for free.
                    </h2>
                  </div>
                  <div className="max-w-[650px]">
                    <p className="text-[16px] leading-[1.32] tracking-[-0.025em] text-white/82 sm:text-[18px]">
                      Claude and ChatGPT can estimate nutrients. Nomsy keeps every logged meal and its nutrients organized over time, so your AI can use your food history without you repeating it.
                    </p>
                    <div className="mt-6 flex flex-wrap items-center gap-2 text-[11px] font-medium uppercase tracking-[0.12em]">
                      <span className="rounded-full border border-white/24 bg-white/8 px-3 py-2 text-white">Claude</span>
                      <span className="rounded-full border border-white/16 px-3 py-2 text-white/66">ChatGPT</span>
                      <span className="rounded-full border border-white/16 px-3 py-2 text-white/66">MCP</span>
                    </div>
                  </div>
                </div>
              </article>

            <div className="nc-mobile-chats">
              <div className="nc-mobile-chat"><h3 className="font-semibold">Connect Nomsy MCP to your Claude.</h3><AiChatDemo assistant="claude" /></div>
              <div className="nc-mobile-chat"><h3 className="font-semibold">Connect Nomsy MCP to your ChatGPT.</h3><AiChatDemo assistant="chatgpt" /></div>
            </div>

              <article className="relative lg:min-h-[430px] overflow-hidden rounded-[24px] border border-black/10 bg-[#dce7d7] px-7 py-8 text-[#1d281b] sm:px-10 sm:py-10 lg:px-12 lg:py-12">
                <div aria-hidden="true" className="pointer-events-none absolute -bottom-20 -right-20 h-[280px] w-[280px] rounded-full border border-[#809476]/25" />
                <div aria-hidden="true" className="pointer-events-none absolute -bottom-7 -right-7 h-[180px] w-[180px] rounded-full border border-[#809476]/25" />
                <div className="relative z-10 flex h-full flex-col gap-6 lg:justify-between lg:gap-16">
                  <div>
                    <h2 className="font-semibold mt-5 max-w-[440px] text-[clamp(2.4rem,5vw,4.5rem)] leading-[0.91] tracking-[-0.025em] text-[#1d281b]">
                      You own your data.
                    </h2>
                  </div>
                  <div className="max-w-[520px]">
                    <p className="text-[16px] leading-[1.32] tracking-[-0.025em] text-[#40503b] sm:text-[18px]">
                      Your data isn’t stuck in Nomsy. Export it whenever you want and use it with the AI you choose.
                    </p>
                  </div>
                </div>
              </article>
            </div>

          </div>
        </section>

        <section className="pb-16 pt-0 lg:pb-24" id="research" aria-labelledby="nutrition-statement-heading" data-testid="template-six-research-panel">
          <div className="rw-container">
            <div className="relative isolate mx-auto max-w-[1240px] overflow-hidden rounded-[32px] bg-[#f2effa] px-7 py-12 sm:px-12 sm:py-16 lg:px-20 lg:py-20">
              <div aria-hidden="true" className="pointer-events-none absolute -right-40 -top-40 -z-10 h-[560px] w-[560px] rounded-full border-[70px] border-white/35" />
              <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-12">
                <div className="min-w-0">
                  <div aria-hidden="true" className="mb-9 flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full bg-[#6555ed]" /><span className="h-px w-12 bg-[#6555ed]/30" /><span className="h-3 w-3 rounded-full bg-[#6555ed]/60" /><span className="h-px w-12 bg-[#6555ed]/30" /><span className="h-3 w-3 rounded-full bg-[#6555ed]/30" />
                  </div>
                  <h2 id="nutrition-statement-heading" className="relative text-[clamp(1.9rem,3vw,2.7rem)] font-medium leading-[1.12] tracking-[-0.025em] text-[#24212e]">
                    Nomsy remembers what you have eaten, understands what your body still needs and turns that into <span className="text-[#5946cc]" style={{ font: "inherit", letterSpacing: "inherit" }}>precise guidance you can act on in seconds.</span>
                  </h2>
                </div>
                <div className="min-w-0"><NomsyProductDemo feature="nutrient-status" compact /></div>
              </div>
            </div>
          </div>
        </section>

        <section className="pb-20 pt-6 lg:pb-28 lg:pt-12" aria-labelledby="testimonials-heading" data-testid="template-six-testimonials">
          <div className="rw-container">
            <div className="mx-auto max-w-[1240px]">
              <h2 id="testimonials-heading" className="font-semibold max-w-[850px] lg:max-w-none text-[clamp(2.5rem,5.5vw,4.6rem)] leading-[0.98] tracking-[-0.025em] text-[#242424]">
                See why everyone&apos;s<br className="hidden sm:block lg:hidden" /> switching to Nomsy
              </h2>
              <TestimonialCarousel>
                {testimonials.map((item, index) => (
                  <figure key={item.name} className={`relative isolate flex min-w-0 flex-col justify-between overflow-hidden rounded-[28px] px-7 pb-7 pt-8 sm:px-9 sm:pb-9 sm:pt-10 lg:min-h-[350px] lg:px-11 lg:pb-10 ${item.tone}`}>
                    {index === 0 ? <div aria-hidden="true" className="pointer-events-none absolute -right-28 -top-36 -z-10 h-[400px] w-[400px] rounded-full border-[60px] border-white/[0.07]" /> : null}
                    <div>
                      <span aria-hidden="true" className="block h-10 font-serif text-[88px] leading-[0.9] opacity-45">“</span>
                      <blockquote className="mt-5 max-w-[600px] text-[clamp(1.65rem,2.8vw,2.6rem)] font-medium leading-[1.12] tracking-[-0.035em]">
                        {item.quote}
                      </blockquote>
                    </div>
                    <figcaption className="mt-9 flex items-center gap-3 border-t border-current/15 pt-5">
                      <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-current/20 text-[13px] font-medium">{item.name.split(" ").map((part) => part[0]).join("")}</span>
                      <span className="text-[15px] font-medium tracking-[-0.01em]">{item.name}</span>
                    </figcaption>
                  </figure>
                ))}
              </TestimonialCarousel>
            </div>
          </div>
        </section>

        <section className="pb-16 pt-4 lg:pb-24 lg:pt-8" id="faq" aria-labelledby="landing-faq-heading" data-testid="landing-faq">
          <div className="rw-container">
            <div className="mx-auto grid max-w-[1240px] gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
              <div>
                <h2 id="landing-faq-heading" className="font-semibold max-w-[380px] text-[clamp(2.3rem,4vw,3.6rem)] leading-[0.98] tracking-[-0.025em] text-[#242424]">A few things you might wonder.</h2>
              </div>
              <div className="min-w-0 border-t border-black/15">
                {LANDING_FAQS.map(({ question, answer }) => (
                  <details key={question} className="group border-b border-black/15">
                    <summary className="flex min-h-[64px] cursor-pointer list-none items-center justify-between gap-5 py-5 text-[18px] leading-snug tracking-[-0.025em] text-[#242424] focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#6157ff] [&::-webkit-details-marker]:hidden">
                      {question}
                      <span aria-hidden="true" className="relative h-4 w-4 shrink-0 text-[#6157ff]">
                        <span className="absolute left-0 top-1/2 h-px w-4 bg-current" />
                        <span className="absolute left-0 top-1/2 h-px w-4 rotate-90 bg-current transition-transform duration-150 group-open:rotate-0 motion-reduce:transition-none" />
                      </span>
                    </summary>
                    <p className="max-w-[620px] pb-6 pr-5 text-[15px] leading-relaxed text-[#6a6a6a]">{answer}</p>
                  </details>
                ))}
                <p className="mt-5 text-[15px] leading-relaxed text-[#6a6a6a]">Still have a question? <a className="text-[#6157ff] underline underline-offset-4" href="/support">We’re here to help.</a></p>
              </div>
            </div>
          </div>
        </section>

        <section className="pb-20 pt-6 lg:pb-28 lg:pt-10" id="company">
          <div className="rw-container">
            <div className="relative mx-auto min-h-[360px] max-w-[1520px] overflow-hidden rounded-[10px] border border-black/10">
              <img
                src="/images/blue-green-gradient.jpg"
                alt="Nomsy team background"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(30,52,41,0.7)_0%,rgba(72,98,88,0.46)_48%,rgba(15,27,23,0.74)_100%)]" />
              <div className="relative z-10 grid min-h-[360px] gap-10 px-8 py-10 text-white lg:grid-cols-[1.1fr_0.9fr] lg:px-16 lg:py-14">
                <div className="self-end">
                  <div className="rw-h4-alt font-semibold tracking-[-0.025em] max-w-[640px] text-white">
                    We are building AI to make food decisions more grounded, more personal and easier to repeat every day.
                  </div>
                </div>
                <div className="flex flex-col justify-end gap-5 lg:items-start">
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-3 rounded-full border border-white/70 bg-white px-5 py-3 text-[15px] font-medium text-[#111111]"
                  >
                    Download on App Store
                    <span aria-hidden="true">›</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer>
        <section className="rw-template6-dark pt-10 text-white min-[1600px]:pt-28" data-testid="template-six-footer">
          <div className="rw-container">
            <div className="relative flex items-end justify-between pb-6">
              <div className="rw-eyebrow text-[#999]">
                © 2026 Nomsy / <a className="hover:underline" href="/legal/terms-of-service">Terms</a> / <a className="hover:underline" href="/legal/privacy-policy">Privacy</a>
              </div>
            </div>
          </div>
        </section>
      </footer>
    </div>
  );
};

const LandingPage = () => {
  useEffect(() => {
    const previousTemplate = document.body.getAttribute("data-template");
    document.body.setAttribute("data-template", "6");

    const preloads = [
      { href: TEMPLATE_SIX_HERO_VIDEO_POSTER, as: "image", type: "image/jpeg" },
      { href: TEMPLATE_SIX_HERO_VIDEO_SRC, as: "video", type: "video/mp4" },
    ].map((asset) => {
      const existing = document.head.querySelector<HTMLLinkElement>(`link[rel="preload"][href="${asset.href}"]`);
      if (existing) return null;

      const link = document.createElement("link");
      link.rel = "preload";
      link.href = asset.href;
      link.as = asset.as;
      link.type = asset.type;
      link.setAttribute("fetchpriority", "high");
      link.setAttribute("data-landing-preload", "true");
      document.head.appendChild(link);
      return link;
    });

    return () => {
      if (document.body.getAttribute("data-template") === "6") {
        if (previousTemplate === null) {
          document.body.removeAttribute("data-template");
        } else {
          document.body.setAttribute("data-template", previousTemplate);
        }
      }

      preloads.forEach((link) => link?.remove());
    };
  }, []);

  return <ExactCopyTemplate />;
};

export default LandingPage;
